import { useState, useRef, useMemo, useEffect, MouseEventHandler } from "react";
// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";
import { prepareColumnDefinitions } from "./prepareColumnDefinitions";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
// Types
import { CustomColDef, ISpiffsBonusTable } from "@/Washouts/types/commons";
import { Issue } from "@/Common/types/tables";

export default function SpiffsBonusTable(props: Readonly<ISpiffsBonusTable>) {
	const { title = "Spiffs", data = [], issues = [], issuesReference = "", addIssues = false, onIssuesUpdatedAction = () => {} } = props;
	const apiRef = useGridApiRef();
	const [columns, setColumns] = useState<CustomColDef[]>([]);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [localIssues, setLocalIssues] = useState<Issue[]>(issues);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...localIssues];

		const isMatchIssue = (currentIssue: Issue, active: Issue) =>
			currentIssue?.rowReference === active?.rowReference && currentIssue?.columnReference === active?.columnReference;

		if (color) {
			let isPresent = false;
			for (const item of currentIssues) {
				if (item && isMatchIssue(item, activeIssue?.current)) {
					item.color = color;
					isPresent = true;
					break;
				}
			}

			if (!isPresent) {
				currentIssues.push({
					color,
					tableReference: props?.issuesReference,
					rowReference: activeIssue?.current?.rowReference,
					columnReference: activeIssue?.current?.columnReference,
				});
			}
		} else {
			currentIssues?.forEach((item, key) => {
				if (isMatchIssue(item, activeIssue?.current)) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setLocalIssues(currentIssues);
		onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	useMemo(() => {
		setColumns(prepareColumnDefinitions(issuesReference, localIssues));
	}, [issuesReference, localIssues]);

	useEffect(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => ({
					...prevState,
					filter: { ...filter, ...cleanFilter },
				}));
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		return () => {
			// Issues controls
			setLocalIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [apiRef]);

	if (!columns) {
		return null;
	}

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={columns}
				rows={data}
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							reward: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: title,
							sx: { mb: "15px" },
						},
					},
					noRowsOverlay: {
						title: `No ${title} Data`,
					},
					cell: issues?.length ? { onClick: handleClickCell } : undefined,
				}}
				getCellClassName={(params) => {
					// Targeting footer
					if (params.id === "auto-generated-group-footer-root") {
						// Use this to change the cell class name depending on the cell value
						const negatives = ["chargebackInvoiceAmount", "saleGross", "unit", "chargebackAmount", "chargebackCommission"];
						if (negatives.includes(params.field) && typeof params.value === "number" && params.value < 0) {
							return "aggregation-footer-negative";
						}

						// Append label 'Total' to the cell value in footer
						if (params.field === "date") {
							return "aggregation-footer-total";
						}
					}
					return "";
				}}
				customProps={{}}
				{...props.tableProps}
			/>
			{addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}
